import { useSelector } from 'react-redux'
import {
  selectLicense,
  selectLicenseFeatures,
  selectLicenseActive,
  selectLicenseValidTo,
  selectLicenseSeatLimit,
  selectLicensePlan,
  selectLicenseInitialized,
} from '../redux/license/licenseSelectors'
import { LicenseFeature } from '../types/license'

const DAYS_BEFORE_EXPIRY_WARNING = 30

export function useLicense() {
  const license = useSelector(selectLicense)
  const features = useSelector(selectLicenseFeatures)
  const active = useSelector(selectLicenseActive)
  const validTo = useSelector(selectLicenseValidTo)
  const seatLimit = useSelector(selectLicenseSeatLimit)
  const plan = useSelector(selectLicensePlan)
  const initialized = useSelector(selectLicenseInitialized)
  const isOwner = license?.isOwner === true

  /**
   * Returns true if the given feature/module is enabled in the current license.
   * Falls back to true when no license has been loaded yet (backend not ready).
   */
  const hasFeature = (feature: LicenseFeature): boolean => {
    if (!initialized || !license) return true
    return features.includes(feature)
  }

  /**
   * Returns true if the license is expired or has been suspended.
   */
  const isExpired = (): boolean => {
    if (!license) return false
    if (!active) return true
    if (!validTo) return false
    return new Date(validTo) < new Date()
  }

  /**
   * Returns the number of days remaining until expiration.
   * Returns null when no license is loaded.
   */
  const daysUntilExpiry = (): number | null => {
    if (!validTo) return null
    const diff = new Date(validTo).getTime() - Date.now()
    return Math.ceil(diff / (1000 * 60 * 60 * 24))
  }

  /**
   * Returns true when the license expires within the warning window (J-30).
   */
  const isExpiringSoon = (): boolean => {
    const days = daysUntilExpiry()
    if (days === null) return false
    return days > 0 && days <= DAYS_BEFORE_EXPIRY_WARNING
  }

  /**
   * Remaining available seats. Returns null when no license is loaded.
   * Note: current usage count must come from the users list in Redux —
   * this hook only exposes the limit from the license.
   */
  const seatsRemaining = (currentUsersCount: number): number | null => {
    if (!license) return null
    return Math.max(0, seatLimit - currentUsersCount)
  }

  return {
    license,
    plan,
    features,
    initialized,
    isOwner,
    hasFeature,
    isExpired,
    isExpiringSoon,
    daysUntilExpiry,
    seatsRemaining,
    seatLimit,
    validTo,
    active,
  }
}
